import type {SubagentHandle, TimelineView} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {ChatState} from '@microsoft/agent-host-protocol'
import type {ContributedInstructionsMetadata} from '@github-ui/ahp-session-client/contracts'
import {createContext, use, useMemo, type ComponentType} from 'react'
import {useAhpSessionEvents} from '../../../hooks/use-ahp-session-events'
import type {Session} from '../../../types/session'
import type {PublicUser} from '../../../types/public-user'
import {
  createPersistedSubagentResolver,
  getSavedRootChats,
  type AhpSessionBundle,
} from '../../../utils/replay-ahp-events'
import {AhpTimelineView, type AhpTimelineViewProps} from './AhpTimelineView'
import {AhpSubagentReference, type AhpSubagentTimelineProps} from './parts/AhpSubagentReference'
import {collectTurnActorIds, extractTurnActor, showAllUserMessages, type ShouldShowUserMessage} from './turn-meta'
import {usePublicUsers} from '../../../hooks/use-public-users'
import {AhpSessionProvider, SavedAhpHistoryProvider} from './AhpSessionContext'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'
import {isConversationRefused} from '../../../utils/conversation-access'
import {useReportConversationRefused} from '../../../contexts/ConversationAccessContext'
import {ConversationLocked} from '../../conversation-access/ConversationLocked'

const emptyMap: ReadonlyMap<number, PublicUser> = new Map()

interface SavedTimelineContextValue {
  bundle: AhpSessionBundle | undefined
  onContributedInstructions?: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
}

interface SavedSubagentTimelineContextValue {
  offline: boolean
  subagent: SubagentHandle
}

const SavedTimelineContext = createContext<SavedTimelineContextValue | undefined>(undefined)
const SavedSubagentTimelineContext = createContext<SavedSubagentTimelineContextValue | undefined>(undefined)

function useSavedTimelineContext(): SavedTimelineContextValue {
  const context = use(SavedTimelineContext)
  if (!context) throw new Error('Saved timeline components must be rendered within AhpSavedTimeline.')
  return context
}

function SavedSubagentTimeline(props: AhpSubagentTimelineProps) {
  const {bundle, onContributedInstructions} = useSavedTimelineContext()
  const context = use(SavedSubagentTimelineContext)
  if (!context) throw new Error('Saved subagent timelines must be rendered within a saved subagent reference.')

  return (
    <AhpSavedTimeline
      {...props}
      state={context.offline ? undefined : props.state}
      bundle={bundle}
      savedResource={context.subagent.resource}
      onContributedInstructions={onContributedInstructions}
    />
  )
}

function HistorySubagent({subagent, offline = false}: {subagent: SubagentHandle; offline?: boolean}) {
  const {bundle} = useSavedTimelineContext()
  const timelineContext = useMemo(() => ({offline, subagent}), [offline, subagent])

  return (
    <SavedSubagentTimelineContext value={timelineContext}>
      <AhpSubagentReference
        subagent={subagent}
        offline={offline}
        persistedState={bundle?.chatStates[subagent.resource]}
        hasPersistedActions={bundle?.chatResourcesWithPersistedActions[subagent.resource] === true}
        TimelineComponent={SavedSubagentTimeline}
      />
    </SavedSubagentTimelineContext>
  )
}

export function AhpSavedTimeline({
  bundle,
  savedResource,
  state,
  ...props
}: Omit<AhpTimelineViewProps, 'savedState' | 'savedTurns'> & {bundle?: AhpSessionBundle; savedResource?: string}) {
  const actorIds = useMemo(
    () => [
      ...collectTurnActorIds(bundle?.chatStates ?? {}),
      ...(state ? collectTurnActorIds({[state.resource]: state}) : []),
      ...Object.values(bundle?.timelineTurns ?? {}).flatMap(turns =>
        turns.flatMap(turn => {
          const actor = extractTurnActor(turn.message)
          return actor ? [actor.githubId] : []
        }),
      ),
    ],
    [bundle, state],
  )
  const profiles = usePublicUsers(actorIds)
  const savedState = savedResource
    ? bundle?.chatStates[savedResource]
    : (state && bundle?.chatStates[state.resource]) || bundle?.defaultChatState
  const resolveSavedSubagent = useMemo(() => bundle && createPersistedSubagentResolver(bundle), [bundle])
  const savedTimelineContext = useMemo(
    () => ({bundle, onContributedInstructions: props.onContributedInstructions}),
    [bundle, props.onContributedInstructions],
  )
  // A new default chat is a separate transcript, not another version of the old root.
  const stateForSavedRoot = state && savedState && state.resource !== savedState.resource ? undefined : state
  const differentResource = !!state?.resource && !!savedState && state.resource !== savedState.resource
  const otherRoots = useMemo(
    () =>
      bundle && !savedResource
        ? getSavedRootChats(bundle).filter(
            chat => chat.resource !== savedState?.resource && chat.resource !== state?.resource,
          )
        : [],
    [bundle, savedResource, savedState?.resource, state?.resource],
  )
  return (
    <SavedTimelineContext value={savedTimelineContext}>
      <AhpSessionProvider profiles={profiles.data ?? emptyMap}>
        {otherRoots.map(chat => (
          <AhpSavedTimeline
            key={chat.resource}
            bundle={bundle}
            savedResource={chat.resource}
            state={undefined}
            view="ready"
            availableModels={props.availableModels}
            onContributedInstructions={props.onContributedInstructions}
          />
        ))}
        <AhpTimelineView
          {...props}
          state={stateForSavedRoot}
          savedState={savedState}
          savedTurns={savedState ? bundle?.timelineTurns?.[savedState.resource] : undefined}
          SubagentComponent={HistorySubagent}
          SavedSubagentComponent={HistorySubagent}
          resolveSavedSubagent={resolveSavedSubagent}
          additionalErrorMessage={props.additionalErrorMessage ?? (savedState ? props.errorMessage : undefined)}
        />
        {differentResource && (
          <AhpSavedTimeline {...props} bundle={bundle} savedResource={state.resource} state={state} />
        )}
      </AhpSessionProvider>
    </SavedTimelineContext>
  )
}

type PersistedTimelineProps = AhpSubagentTimelineProps & {
  bundle: AhpSessionBundle
  availableModels?: AhpTimelineViewProps['availableModels']
  onContributedInstructions?: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
  loadingPresentation?: 'local' | 'page'
}

const PersistedTimelineContext = createContext<
  Pick<PersistedTimelineProps, 'bundle' | 'onContributedInstructions'> | undefined
>(undefined)

function usePersistedTimelineContext() {
  const context = use(PersistedTimelineContext)
  if (!context) throw new Error('Persisted timeline components must be rendered within AhpPersistedTimeline.')
  return context
}

function PersistedSubagentTimeline(props: AhpSubagentTimelineProps) {
  const {bundle, onContributedInstructions} = usePersistedTimelineContext()
  return <AhpPersistedTimeline {...props} bundle={bundle} onContributedInstructions={onContributedInstructions} />
}

function PersistedSubagent({subagent}: {subagent: SubagentHandle}) {
  const {bundle} = usePersistedTimelineContext()
  return (
    <AhpSubagentReference
      subagent={subagent}
      TimelineComponent={PersistedSubagentTimeline}
      offline
      hasPersistedActions={bundle.chatResourcesWithPersistedActions[subagent.resource] === true}
      {...(bundle.chatStates[subagent.resource] ? {persistedState: bundle.chatStates[subagent.resource]} : {})}
    />
  )
}

export interface AhpSessionHistoryHeaderContext {
  chatState: ChatState | undefined
  profiles: ReadonlyMap<number, PublicUser>
}

export interface AhpSessionHistoryTimelineProps {
  session: Session
  ariaLabel?: string
  refetchInterval?: number | false | null
  shouldShowUserMessage?: ShouldShowUserMessage
  HeaderComponent?: ComponentType<AhpSessionHistoryHeaderContext>
  onContributedInstructions?: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
  loadingPresentation?: 'local' | 'page'
}

function resolveHistoryView(isLoading: boolean, isError: boolean): TimelineView {
  if (isError) return 'error'
  if (isLoading) return 'loading'
  return 'ready'
}

export function AhpPersistedTimeline({
  bundle,
  state,
  view,
  errorMessage,
  additionalErrorMessage,
  ariaLabel,
  onContributedInstructions,
  loadingPresentation,
  availableModels,
}: PersistedTimelineProps) {
  const resolveSubagent = useMemo(() => createPersistedSubagentResolver(bundle), [bundle])
  const persistedTimelineContext = useMemo(
    () => ({bundle, onContributedInstructions}),
    [bundle, onContributedInstructions],
  )

  return (
    <PersistedTimelineContext value={persistedTimelineContext}>
      <SavedAhpHistoryProvider saved>
        <AhpTimelineView
          state={state}
          view={view}
          ariaLabel={ariaLabel}
          SubagentComponent={PersistedSubagent}
          resolveSubagent={resolveSubagent}
          onContributedInstructions={onContributedInstructions}
          loadingPresentation={loadingPresentation}
          loadingSourceKind="history"
          availableModels={availableModels}
          {...(errorMessage ? {errorMessage} : {})}
          {...(additionalErrorMessage ? {additionalErrorMessage} : {})}
        />
      </SavedAhpHistoryProvider>
    </PersistedTimelineContext>
  )
}

/**
 * Read-only history timeline for AHP sessions the live relay can't replay,
 * rebuilt from persisted events and rendered with the live timeline's UI.
 */
export function AhpSessionHistoryTimeline({
  session,
  ariaLabel,
  refetchInterval = null,
  shouldShowUserMessage = showAllUserMessages,
  HeaderComponent,
  onContributedInstructions,
  loadingPresentation = 'local',
}: AhpSessionHistoryTimelineProps) {
  const {models} = useAgentModels()
  const {sessionBundle, chatState, isLoading, isError, error, refetch} = useAhpSessionEvents({
    session,
    refetchInterval: refetchInterval ?? false,
  })
  const actorIds = useMemo(() => collectTurnActorIds(sessionBundle?.chatStates ?? {}), [sessionBundle])
  const actorUsers = usePublicUsers(actorIds)
  const view = resolveHistoryView(isLoading, isError)
  const profiles = actorUsers.data ?? emptyMap
  const refused = isConversationRefused(error)
  useReportConversationRefused(session.task_id, refused)

  if (refused) return <ConversationLocked surface="ahp-history" onRetry={() => void refetch()} />

  return (
    <AhpSessionProvider profiles={profiles} shouldShowUserMessage={shouldShowUserMessage}>
      {HeaderComponent && <HeaderComponent chatState={chatState} profiles={profiles} />}
      {sessionBundle ? (
        <AhpPersistedTimeline
          bundle={sessionBundle}
          state={chatState}
          view={view}
          ariaLabel={ariaLabel ?? 'Session timeline'}
          {...(error ? {errorMessage: error.message} : {})}
          onContributedInstructions={onContributedInstructions}
          loadingPresentation={loadingPresentation}
          availableModels={models}
        />
      ) : (
        <AhpTimelineView
          state={chatState}
          view={view}
          subagentChatsEnabled={false}
          {...(error ? {errorMessage: error.message} : {})}
          {...(ariaLabel ? {ariaLabel} : {})}
          onContributedInstructions={onContributedInstructions}
          loadingPresentation={loadingPresentation}
          loadingSourceKind="history"
          availableModels={models}
        />
      )}
    </AhpSessionProvider>
  )
}
